
/**
 * TypesSet class to manage sets of the different types of components
 * used in projects.
 */
export class TypesSet {
  // Static list of all type categories
  static get categories() {
    return [
      'wallTypes',
      'roofTypes',
      'windowTypes',
      'interiorShadingTypes',
      'exteriorShadingTypes',
      'doorTypes',
      'skylightTypes',
      'bufferSpaceTypes',
      'schedules',
      'systems',
      'spaces',
      'customMaterials'
    ];
  }
  
  init() {
    // Create sets for all the different types
    for (const category of TypesSet.categories) {
      this[category] = new Set();
    }
  }

  static create() {
    const typesSet = new TypesSet();
    typesSet.init();
    return typesSet;
  }

  /**
   * Add a single type to the appropriate set
   * @param {Object} type - The type object to add
   * @param {string} typeCategory - The category of the type (e.g., 'wallTypes')
   */
  addType(type, typeCategory) {
    if (this[typeCategory] instanceof Set) {
      this[typeCategory].add(type);
    } else {
      console.error(`Invalid type category: ${typeCategory}`);
    }
  }

  /**
   * Add all types from another TypesSet object
   * @param {TypesSet} otherTypesSet - Another TypesSet object to add types from
   */
  addTypes(otherTypesSet) {
    // For each category in the TypesSet
    for (const category of TypesSet.categories) {
      // Add all items from the other set to this set
      if (otherTypesSet[category] instanceof Set) {
        for (const item of otherTypesSet[category]) {
          this[category].add(item);
        }
      }
    }
  }

  /**
   * Remove all types from another TypesSet object
   * @param {TypesSet} otherTypesSet - Another TypesSet object to remove types from
   */
  removeTypes(otherTypesSet) {
    // For each category in the TypesSet
    for (const category of TypesSet.categories) {
      // Remove all items from the other set from this set
      if (otherTypesSet[category] instanceof Set) {
        for (const item of otherTypesSet[category]) {
          this[category].delete(item);
        }
      }
    }
  }

  /**
   * Check if a type exists in the appropriate set
   * @param {Object} type - The type object to check
   * @param {string} typeCategory - The category of the type
   * @returns {boolean} - Whether the type exists in the set
   */
  hasType(type, typeCategory) {
    if (this[typeCategory] instanceof Set) {
      return this[typeCategory].has(type);
    }
    return false;
  }

  /**
   * Get the size of a specific type set
   * @param {string} typeCategory - The category of the type
   * @returns {number} - The number of items in the set
   */
  getTypeCount(typeCategory) {
    if (this[typeCategory] instanceof Set) {
      return this[typeCategory].size;
    }
    return 0;
  }

  /**
   * Clear all types from a specific category
   * @param {string} typeCategory - The category to clear
   */
  clearTypes(typeCategory) {
    if (this[typeCategory] instanceof Set) {
      this[typeCategory].clear();
    }
  }

  /**
   * Clear all types from all categories
   */
  clearAll() {
    for (const category of TypesSet.categories) {
      this[category].clear();
    }
  }

  /**
   * Pretty log all types in the set
   * @param {Object} opts - Options for logging
   * @param {boolean} opts.detailed - Whether to show detailed information
   * @param {boolean} opts.onlyNonEmpty - Whether to only show non-empty categories
   * @returns {string} - A formatted string representation of all types
   */
  prettyLog(opts = {}) {
    const detailed = opts.detailed || false;
    const onlyNonEmpty = opts.onlyNonEmpty || false;
    
    let result = 'TypesSet Contents:\n';
    
    const categories = TypesSet.categories;
    
    for (const category of categories) {
      const set = this[category];
      
      // Skip empty categories if onlyNonEmpty is true
      if (onlyNonEmpty && set.size === 0) {
        continue;
      }
      
      result += `\n${category} (${set.size}):\n`;
      
      if (set.size === 0) {
        result += '  <empty>\n';
      } else {
        let index = 1;
        for (const item of set) {
          const name = item.name?.value || '<unnamed>';
          
          if (detailed) {
            result += `  ${index}. ${name} (${typeof item})\n`;
            // Add more detailed properties if needed
            if (item.id) {
              result += `     id: ${item.id}\n`;
            }
          } else {
            result += `  ${index}. ${name}\n`;
          }
          
          index++;
        }
      }
    }
    
    return result;
  }

  /**
   * Returns a flat list of all elements from all sets
   * @returns {Array} - Array containing all elements from all sets
   */
  toList() {
    const result = [];
    
    for (const category of TypesSet.categories) {
      const set = this[category];
      for (const item of set) {
        result.push(item);
      }
    }
    
    return result;
  }
}
